import { useState, useEffect, useRef } from "react";
import { Link } from "react-router-dom";
import { Button } from "../components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { 
  ArrowLeft, FileText, Upload, Download, Trash2, 
  Eye, File, Image, FileArchive, X 
} from "lucide-react";
import { NestIcon } from "../components/NestIcon";
import { useAuth, API } from "../App";
import axios from "axios";
import { toast } from "sonner";

export default function Documents() {
  const { token } = useAuth();
  const [loading, setLoading] = useState(true);
  const [documents, setDocuments] = useState([]);
  const [contracts, setContracts] = useState([]);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  useEffect(() => {
    fetchDocuments();
  }, []);

  const fetchDocuments = async () => {
    try {
      const [docsRes, contractsRes] = await Promise.all([
        axios.get(`${API}/contracts/documents`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/contracts/`, { headers: { Authorization: `Bearer ${token}` } })
      ]);
      setDocuments(docsRes.data);
      setContracts(contractsRes.data);
    } catch (error) {
      console.error("Error fetching documents:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleFileUpload = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    setUploading(true);
    const formData = new FormData();
    formData.append("file", file);

    try {
      await axios.post(`${API}/contracts/upload?document_type=general`, formData, {
        headers: { 
          Authorization: `Bearer ${token}`,
          "Content-Type": "multipart/form-data"
        }
      });
      toast.success("Document uploaded successfully");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to upload document");
    } finally {
      setUploading(false);
      if (fileInputRef.current) {
        fileInputRef.current.value = "";
      }
    }
  };

  const handleDownload = async (documentId, filename) => {
    try {
      const response = await axios.get(`${API}/contracts/documents/${documentId}/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
    } catch (error) {
      toast.error("Failed to download document");
    }
  };

  const handleDelete = async (documentId) => {
    if (!confirm("Are you sure you want to delete this document?")) return;
    
    try {
      await axios.delete(`${API}/contracts/documents/${documentId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success("Document deleted");
      fetchDocuments();
    } catch (error) {
      toast.error("Failed to delete document");
    }
  };

  const handleDownloadContract = async (policyId) => {
    try {
      const response = await axios.get(`${API}/policies/${policyId}/contract/download`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `TheRentPayer_Contract_${policyId}.html`);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      toast.success("Contract downloaded");
    } catch (error) {
      toast.error("Failed to download contract");
    }
  };

  const getFileIcon = (contentType) => {
    if (contentType?.startsWith("image/")) return <Image className="h-5 w-5 text-blue-500" />;
    if (contentType?.includes("pdf")) return <FileText className="h-5 w-5 text-red-500" />;
    if (contentType?.includes("zip") || contentType?.includes("archive")) return <FileArchive className="h-5 w-5 text-amber-500" />;
    return <File className="h-5 w-5 text-gray-500" />;
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      {/* Header */}
      <header className="glass-header sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center h-16">
            <Link to="/" className="flex items-center gap-2">
              <NestIcon className="h-8 w-8 text-primary" />
              <span className="text-xl font-bold text-primary" style={{ fontFamily: 'Young Serif, serif' }}>
                TheRentPayer
              </span>
            </Link>
            <Link to="/dashboard">
              <Button variant="ghost">Back to Dashboard</Button>
            </Link>
          </div>
        </div>
      </header>

      <main className="max-w-4xl mx-auto px-4 py-8">
        <Link to="/dashboard" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground mb-6">
          <ArrowLeft className="h-4 w-4" />
          Back to Dashboard
        </Link>

        <div className="flex justify-between items-start mb-8">
          <div>
            <h1 className="text-3xl font-bold text-foreground mb-2" style={{ fontFamily: 'Young Serif, serif' }}>
              Documents & Contracts
            </h1>
            <p className="text-muted-foreground">
              View your contracts and upload supporting documents
            </p>
          </div>
          <Button 
            className="btn-primary"
            onClick={() => fileInputRef.current?.click()}
            disabled={uploading}
            data-testid="upload-document-btn"
          >
            <Upload className="h-4 w-4 mr-2" />
            {uploading ? "Uploading..." : "Upload Document"}
          </Button>
          <input
            type="file"
            ref={fileInputRef}
            onChange={handleFileUpload}
            className="hidden"
            accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.txt"
          />
        </div>

        {/* Policy Contracts */}
        <section className="mb-8">
          <h2 className="text-xl font-bold text-foreground mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
            Policy Contracts
          </h2>
          
          {contracts.length > 0 ? (
            <div className="space-y-4">
              {contracts.map((contract) => (
                <Card key={contract.contract_id} className="card-organic">
                  <CardContent className="p-6">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-4">
                        <div className="bg-primary/10 p-3 rounded-lg">
                          <FileText className="h-6 w-6 text-primary" />
                        </div>
                        <div>
                          <h3 className="font-semibold">Policy Contract</h3>
                          <p className="text-sm text-muted-foreground font-mono">{contract.policy_id}</p>
                          <p className="text-xs text-muted-foreground">
                            Created: {new Date(contract.created_at).toLocaleDateString()}
                          </p>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <Badge className="bg-green-100 text-green-800">{contract.status}</Badge>
                        <Button 
                          variant="outline" 
                          size="sm"
                          onClick={() => handleDownloadContract(contract.policy_id)}
                          data-testid={`download-contract-${contract.policy_id}`}
                        >
                          <Download className="h-4 w-4 mr-1" />
                          Download
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-organic">
              <CardContent className="p-8 text-center">
                <p className="text-muted-foreground">No active contracts yet. Get a quote to start!</p>
              </CardContent>
            </Card>
          )}
        </section>

        {/* Uploaded Documents */}
        <section>
          <h2 className="text-xl font-bold text-foreground mb-4" style={{ fontFamily: 'Young Serif, serif' }}>
            Your Documents
          </h2>

          {documents.length > 0 ? (
            <div className="space-y-3">
              {documents.map((doc) => (
                <Card key={doc.document_id} className="card-organic">
                  <CardContent className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        {getFileIcon(doc.content_type)}
                        <div>
                          <p className="font-medium truncate max-w-[300px]">{doc.filename}</p>
                          <div className="flex items-center gap-2 text-xs text-muted-foreground">
                            <span>{(doc.size / 1024).toFixed(1)} KB</span>
                            <span>•</span>
                            <span>{new Date(doc.uploaded_at).toLocaleDateString()}</span>
                            <span>•</span>
                            <Badge variant="outline" className="text-xs">{doc.document_type}</Badge>
                          </div>
                        </div>
                      </div>
                      <div className="flex gap-2">
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDownload(doc.document_id, doc.filename)}
                          data-testid={`download-doc-${doc.document_id}`}
                        >
                          <Download className="h-4 w-4" />
                        </Button>
                        <Button 
                          variant="ghost" 
                          size="icon"
                          onClick={() => handleDelete(doc.document_id)}
                          className="text-destructive hover:text-destructive"
                          data-testid={`delete-doc-${doc.document_id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </div>
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          ) : (
            <Card className="card-organic">
              <CardContent className="p-8 text-center">
                <div className="bg-muted rounded-full w-16 h-16 flex items-center justify-center mx-auto mb-4">
                  <Upload className="h-8 w-8 text-muted-foreground" />
                </div>
                <h3 className="text-lg font-semibold mb-2">No Documents Yet</h3>
                <p className="text-muted-foreground mb-4">
                  Upload important documents like lease agreements, employment letters, etc.
                </p>
                <Button className="btn-primary" onClick={() => fileInputRef.current?.click()}>
                  <Upload className="h-4 w-4 mr-2" />
                  Upload Your First Document
                </Button>
              </CardContent>
            </Card>
          )}
        </section>
      </main>
    </div>
  );
}
